import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectAdminToken } from "../../admin/sessionSlice";
import { fetchThemeData } from "../../admin/themeSlice";
import { setBackButtonUrl } from "../uiSlice";
import { s3ThemeAssetUrl } from "../../utils/themeAssets";
import { apiUrl } from "../../utils/apiUrl";
import { themeSurfaceStyle } from "../../utils/colorUtils";
import { isVotingOpen } from "../../utils/yogaTheme";
import YogaLeaderboardModal from "./YogaLeaderboardModal";
import YogaMediaPreviewModal from "./YogaMediaPreviewModal";
import "./yogaGallery.css";

const PAGE_SIZE = 8;

export default function YogaGallery() {
  const dispatch = useDispatch();
  const { data: themeData, status, error: themeError } = useSelector((s) => s.theme);
  const user = useSelector((s) => s.auth.user);
  const adminToken = useSelector(selectAdminToken);

  const [uploads, setUploads] = useState([]);
  const [sort, setSort] = useState("most_liked");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [likingId, setLikingId] = useState(null);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);
  const [votingOpen, setVotingOpen] = useState(true);

  const titleColor = themeData?.text_color || "#1a2b6d";
  const surfaceStyle = useMemo(() => themeSurfaceStyle(themeData), [themeData]);
  const showLikes = isVotingOpen(themeData) && votingOpen;
  const btnStyle = {
    ...(themeData?.button_backgroundcolor && {
      backgroundColor: themeData.button_backgroundcolor,
    }),
    ...(themeData?.button_color && { color: themeData.button_color }),
    ...(themeData?.button_bordercolor && { borderColor: themeData.button_bordercolor }),
    ...(themeData?.button_color && { border: `2px solid ${themeData.button_color}` }),
  };

  const totalPages = Math.max(1, Math.ceil(uploads.length / PAGE_SIZE));
  const pageItems = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return uploads.slice(start, start + PAGE_SIZE);
  }, [uploads, page]);

  useEffect(() => {
    dispatch(setBackButtonUrl(user?.backButtonRedirect || "/login?&save=true"));
  }, [dispatch, user?.backButtonRedirect]);

  useEffect(() => {
    setPage(1);
  }, [sort]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const loadGallery = useCallback(async () => {
    if (!adminToken) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const resp = await fetch(apiUrl("/yogaGallery"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          userId: user?.userId || "",
          sort,
        }),
      });
      const payload = await resp.json();
      if (resp.ok && payload?.success) {
        setUploads(Array.isArray(payload.uploads) ? payload.uploads : []);
        if (typeof payload.votingOpen === "boolean") {
          setVotingOpen(payload.votingOpen);
        }
      } else {
        setUploads([]);
      }
    } catch (e) {
      console.error("yogaGallery", e);
      setUploads([]);
    } finally {
      setLoading(false);
    }
  }, [adminToken, user?.userId, sort]);

  useEffect(() => {
    loadGallery();
  }, [loadGallery]);

  const handleLike = async (gameId) => {
    if (!showLikes || !user || !adminToken || likingId) return;
    setLikingId(gameId);
    try {
      const resp = await fetch(apiUrl("/yogaLike"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({ userId: user.userId, gameId }),
      });
      const payload = await resp.json();
      if (resp.ok && payload?.success) {
        setUploads((prev) =>
          prev.map((item) =>
            item.id === gameId
              ? { ...item, likedByMe: payload.liked, likeCount: payload.likeCount }
              : item
          )
        );
      }
    } catch (e) {
      console.error("yogaLike", e);
    } finally {
      setLikingId(null);
    }
  };

  if (status === "loading" || status === "idle") {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader" />
      </div>
    );
  }

  if (status === "failed" || !themeData) {
    return (
      <div className="yg-page">
        <p style={{ textAlign: "center", color: titleColor }}>
          Could not load game data.
          {themeError ? ` (${themeError})` : ""}
        </p>
        <button
          type="button"
          className="yg-sort-select"
          style={{ display: "block", margin: "16px auto" }}
          onClick={() => dispatch(fetchThemeData({ themeId: null }))}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="yg-page">
      <div className="yg-actions">
        <button
          type="button"
          className="yg-leaderboard-btn"
          style={btnStyle.backgroundColor || btnStyle.color ? btnStyle : undefined}
          onClick={() => setShowLeaderboard(true)}
        >
          Leaderboard
        </button>
      </div>

      <div
        className={`yg-panel${surfaceStyle ? " yg-surface--themed" : ""}`}
        style={surfaceStyle}
      >
        <div className="yg-header">
          <h1 className="yg-title" style={{ color: titleColor }}>
            Yoga Gallery
          </h1>
          <select
            className="yg-sort-select"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            aria-label="Sort uploads"
          >
            <option value="most_liked">Most Liked</option>
            <option value="newest">Newest</option>
          </select>
        </div>

        {loading ? (
          <div className="quiz-loader-container yg-loader">
            <div className="quiz-loader" />
          </div>
        ) : uploads.length === 0 ? (
          <p className="yg-empty">No uploads yet. Be the first to share your yoga pose!</p>
        ) : (
          <>
            <div className="yg-grid">
              {pageItems.map((item) => {
                const mediaUrl = s3ThemeAssetUrl(item.uploadFile);
                const cardKey = item.galleryKey || String(item.id);
                return (
                  <article className="yg-card" key={cardKey}>
                    <div className="yg-media-wrap">
                      <button
                        type="button"
                        className="yg-media-open"
                        onClick={() => setPreviewItem(item)}
                        aria-label={`View upload by ${item.name || "player"}`}
                      >
                        {item.mediaType === "video" ? (
                          <>
                            <video
                              src={mediaUrl}
                              className="yg-media"
                              preload="metadata"
                              playsInline
                              tabIndex={-1}
                            />
                            <span className="yg-play-badge" aria-hidden>
                              ▶
                            </span>
                          </>
                        ) : (
                          <img src={mediaUrl} alt={item.name || "Yoga upload"} className="yg-media" />
                        )}
                      </button>
                      {showLikes ? (
                        <button
                          type="button"
                          className={`yg-like-pill${item.likedByMe ? " yg-like-pill--active" : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLike(item.id);
                          }}
                          disabled={likingId === item.id}
                          aria-label={`Like upload by ${item.name || "player"}`}
                        >
                          <span className="yg-heart" aria-hidden>
                            ♥
                          </span>
                          <span>{item.likeCount}</span>
                        </button>
                      ) : null}
                    </div>
                    {item.name ? <p className="yg-name">{item.name}</p> : null}
                  </article>
                );
              })}
            </div>

            {totalPages > 1 && (
              <nav className="yg-pagination" aria-label="Gallery pages">
                <button
                  type="button"
                  className="yg-page-btn"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Prev
                </button>
                <span className="yg-page-info">
                  Page {page} of {totalPages}
                </span>
                <button
                  type="button"
                  className="yg-page-btn"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </button>
              </nav>
            )}
          </>
        )}

        <p className="yg-footer-msg">
          <span aria-hidden>❤️</span> Spread love. Stay inspired. Keep practicing.
        </p>
      </div>

      <YogaLeaderboardModal
        open={showLeaderboard}
        onClose={() => setShowLeaderboard(false)}
        surfaceStyle={surfaceStyle}
        titleColor={titleColor}
        highlightStyle={{
          ...(themeData?.button_backgroundcolor && {
            backgroundColor: themeData.button_backgroundcolor,
          }),
          ...(themeData?.button_color && { color: themeData.button_color }),
        }}
      />

      <YogaMediaPreviewModal
        item={previewItem}
        onClose={() => setPreviewItem(null)}
        titleColor={titleColor}
      />
    </div>
  );
}
